import { WEBSOCKET_MESSAGES } from '@server/websocket-messages';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { useSweepNotifications } from '../components/notifications/useSweepNotifications';
import { useSfChangeFeed } from '../components/pages/salesforce-logs/change-feed/useSfChangeFeed';
import { setDeploying, showDeploymentReport } from '../reducers/devOpsCenterReducer';
import { selectCrmOrgs, setCrmOrg } from '../components/pages/environments/environmentsReducer';
import {
  setSlackIntegrationStatus,
  setTeamsIntegrationStatus,
  SlackConnectionStatus,
  TeamsConnectionStatus,
} from '../reducers/integrationsReducer';
import { setNotificationsHasUpdates } from '../reducers/notificationsCenterReducers';
import { SweepNotificationVariant } from '../reducers/notificationsReducer';
import { updateReport } from '../reducers/reportsReducer';
import { displayChangedRows } from '../reducers/sfChangeFeedReducer';
import { requestRefreshGlobal } from '../reducers/global/globalReducer';
import { setShouldPillsRecalculate } from '../components/canvas-pills/canvasPillsReducer';
import { useFetchGlobal } from '../hooks/global-reducer/useFetchGlobal';
import { useCallback } from 'react';
import { deploymentCompleted } from '../reducers/deploymentsReducer';
import { AutomationDeploySuccessEventMap } from '../components/Automations/helper';
import { ACTIONS_EVENTS } from '../services/events';
import useSendBiEvent from '../hooks/useSendBiEvent';
import { setConnectionState } from '../reducers/connectOrgReducer';
import { useOrgAnalysis } from '../components/pages/get-started/user-homepage/useOrgAnalysis';
import { selectUserAccountId } from '../reducers/userInfoReducer';
import type { ScanUpdate } from '../components/issues/issuesTypes';
import {
  updateAgentScanStatus,
  updateAgentFileStatus,
} from '../components/aiAgentCenter/agentsReducer';
import { useFunnelMaps } from '../components/pages/funnel-maps/useFunnelMaps';
import { setFunnelStatusFromFunnel } from '../reducers/funnelStatusReducer';
import type { FunnelDeploymentSocketData } from './types';
import {
  setMetadataMappingCompleted,
  setMetadataMappingError,
  addChatMessage,
} from '../reducers/metadataMappingReducer';
import {
  addAsyncResponseChunk,
  addAsyncResponse,
  finishAsyncResponse,
  setResearchInProgress,
  setChatName,
} from '../components/aiAgentCenter/agentsChatsReducer';
import { setSimulationResult } from '../reducers/simulationReducer';
import { appRoutes } from '../constants/appRoutes';
import type { CrmOrg } from '../types/CrmOrgs';
import { DateTime } from 'luxon';
import type { AiChatMessageContent } from '@server/ai';
import { AgentFileStatus, ChatCompletionRequestMessageRole } from '@server/ai';
import {
  useDefaultEnvId,
  useDefaultEnv,
} from '../components/pages/environments/default-env-context/DefaultEnvContext';

const DEPLOY_SUCCESS = 'Successfully deployed';
const activationCompleteMessages = [WEBSOCKET_MESSAGES.AUTOMATIONS_TOGGLE_ACTIVATION_COMPLETED];

const useSocketHandlers = () => {
  const sendBiEvent = useSendBiEvent();
  const dispatch = useDispatch();
  const defaultEnv = useDefaultEnv();
  const environments = useSelector(selectCrmOrgs);
  const { addNotification } = useSweepNotifications();
  const { compareOldFeedWithNewData } = useSfChangeFeed({});
  const { fetchOrgAnalysis } = useOrgAnalysis();
  const crmOrgId = useDefaultEnvId();
  const { fetchFunnelMaps } = useFunnelMaps();
  const { pathname } = useLocation();
  const accountId = useSelector(selectUserAccountId);
  const { fetchGlobal } = useFetchGlobal();

  const getCrmOrgById = useCallback(
    (crmOrgId: string) => environments.find((env) => env.id === crmOrgId),
    [environments],
  );

  const fetchCompletedHandler = (payload: any) => {
    const { data, success } = payload;
    const { crmOrg, notifyUser } = data as { crmOrg: CrmOrg; notifyUser: boolean };
    const _message = success
      ? `${crmOrg.name} environment was successfully refreshed`
      : `${crmOrg.name} environment has failed to refresh`;

    if (notifyUser) {
      addNotification({
        variant: success ? SweepNotificationVariant.Success : SweepNotificationVariant.Error,
        message: _message,
      });
    }

    dispatch(setCrmOrg({ crmOrg }));
    dispatch(
      setConnectionState({
        connectionState: { type: 'fetchStatus', fetchStatus: crmOrg.fetchStatus },
      }),
    );
    dispatch(requestRefreshGlobal({ crmOrgId: crmOrg.id }));
  };

  const slackConnectionCompletedHandler = (message: any) => {
    if (message.success) {
      addNotification({
        variant: SweepNotificationVariant.Success,
        message: 'Slack integration is connected now',
      });
      dispatch(setSlackIntegrationStatus(SlackConnectionStatus.Connected));
    } else {
      addNotification({
        variant: SweepNotificationVariant.Error,
        message: 'Slack integration failed to connect',
      });
      dispatch(setSlackIntegrationStatus(SlackConnectionStatus.Error));
    }
  };

  const teamsConnectionCompletedHandler = (message: any) => {
    if (message.success) {
      addNotification({
        variant: SweepNotificationVariant.Success,
        message: 'Teams integration is connected now',
      });
      dispatch(setTeamsIntegrationStatus(TeamsConnectionStatus.Connected));
    } else {
      addNotification({
        variant: SweepNotificationVariant.Error,
        message: 'Teams integration failed to connect',
      });
      dispatch(setTeamsIntegrationStatus(TeamsConnectionStatus.Error));
    }
  };

  const slackDisconnectionCompletedHandler = (message: any) => {
    if (message.success) {
      addNotification({
        variant: SweepNotificationVariant.Error,
        message: 'Slack integration is no longer connected',
      });
      dispatch(setSlackIntegrationStatus(SlackConnectionStatus.NotInitialized));
    } else {
      addNotification({
        variant: SweepNotificationVariant.Error,
        message: 'Failed to disconnect Slack integration',
      });
      dispatch(setSlackIntegrationStatus(SlackConnectionStatus.Error));
    }
  };

  const teamsDisconnectionCompletedHandler = (message: any) => {
    if (message.success) {
      addNotification({
        variant: SweepNotificationVariant.Error,
        message: 'Teams integration is no longer connected',
      });
      dispatch(setTeamsIntegrationStatus(TeamsConnectionStatus.NotInitialized));
    } else {
      addNotification({
        variant: SweepNotificationVariant.Error,
        message: 'Failed to disconnect Teams integration',
      });
    }
  };
  const reportingActivationCompletedHandler = (response: any) => {
    const report = response.data;
    const message = response.success
      ? `${report?.name || 'Report'} is active in Salesforce`
      : `Activation failed`;

    report && dispatch(updateReport({ newReport: report }));

    addNotification({
      variant: response.success ? SweepNotificationVariant.Success : SweepNotificationVariant.Error,
      message,
    });
  };

  const sfChangeFeedNotificationReceived = async () => {
    if (await compareOldFeedWithNewData()) {
      addNotification({
        variant: SweepNotificationVariant.Default,
        message: `Recent changes are ready to review`,
        action: {
          reduxAction: {
            type: displayChangedRows.type,
          },
          actionText: 'Show changes',
        },
      });
    }
  };

  const notificationsNewHandler = useCallback(() => {
    dispatch(setNotificationsHasUpdates());
    if (!defaultEnv) {
      return;
    }
    dispatch(requestRefreshGlobal({ crmOrgId: defaultEnv.id }));
  }, [defaultEnv, dispatch]);

  const automationsDeployCompletedHandler = useCallback(
    (
      results: {
        success: true;
        error_message?: string;
        data: {
          crmOrgId: string;
          isActive?: boolean;
          automationType?: AutomationType;
        };
      }[],
      msgName: WEBSOCKET_MESSAGES,
    ) => {
      if (!defaultEnv) {
        return;
      }
      dispatch(requestRefreshGlobal({ crmOrgId: defaultEnv.id }));
      const allSuccess = results.every((res) => res.success);
      const allFailed = results.every((res) => !res.success);
      const isActivation = activationCompleteMessages.includes(msgName) && results[0].data.isActive;
      const isDeactivation =
        activationCompleteMessages.includes(msgName) && !results[0].data.isActive;

      if (allSuccess) {
        dispatch(setShouldPillsRecalculate(true));
        let message = DEPLOY_SUCCESS;
        if (isActivation || isDeactivation) {
          message = `Successfully ${isActivation ? 'activated' : 'deactivated'}`;
        } else {
          if (results[0].data.automationType) {
            sendBiEvent({ name: AutomationDeploySuccessEventMap[results[0].data.automationType] });
          }
        }
        return addNotification({
          keepOpen: false,
          variant: SweepNotificationVariant.Success,
          message,
        });
      }
      if (allFailed) {
        let message = `Failed to deploy due to technical issues. Changes were saved`;
        if (isActivation || isDeactivation) {
          message = `Failed to ${
            isActivation ? 'activated' : 'deactivated'
          } due to technical issues`;
        }
        return addNotification({
          keepOpen: true,
          variant: SweepNotificationVariant.Error,
          message,
        });
      }
      results.forEach((res) => {
        const { data } = res;
        const crmOrg = getCrmOrgById(data.crmOrgId);
        const isDefault = defaultEnv.id === crmOrg?.id;
        const crmOrgName = crmOrg?.name;
        let message = res.success
          ? `${DEPLOY_SUCCESS} to ${crmOrgName}`
          : `Failed to deploy to ${crmOrgName} due to technical issues${isDefault ? '. Changes were saved' : ''}`;

        if (isActivation || isDeactivation) {
          message = res.success
            ? `Successfully ${isActivation ? 'activated' : 'deactivated'} in ${crmOrgName}`
            : `Failed to ${
                isActivation ? 'activate' : 'deactivate'
              } in ${crmOrgName} due to technical issues`;
        }
        addNotification({
          keepOpen: !res.success,
          variant: res.success ? SweepNotificationVariant.Success : SweepNotificationVariant.Error,
          message,
        });
      });
    },
    [defaultEnv, dispatch, addNotification, sendBiEvent, getCrmOrgById],
  );

  const rollupsDeployCompletedHandler = useCallback(
    (
      results: {
        success: true;
        error_message?: string;
        data: {
          crmOrgId: string;
        };
      }[],
    ) => {
      if (!defaultEnv) {
        return;
      }
      dispatch(requestRefreshGlobal({ crmOrgId: defaultEnv.id }));
      const allSuccess = results.every((res) => res.success);

      if (allSuccess) {
        sendBiEvent({ name: ACTIONS_EVENTS.rollupsDeploySuccess });
        const message = DEPLOY_SUCCESS;
        return addNotification({
          keepOpen: false,
          variant: SweepNotificationVariant.Success,
          message,
        });
      }

      results.forEach((res) => {
        const { data } = res;
        const crmOrg = getCrmOrgById(data.crmOrgId);
        const isDefault = defaultEnv.id === crmOrg?.id;
        const crmOrgName = crmOrg?.name;
        const message = res.success
          ? `${DEPLOY_SUCCESS} to ${crmOrgName}`
          : `Failed to deploy to ${crmOrgName}${isDefault ? '. Changes were saved' : ''}`;

        addNotification({
          keepOpen: !res.success,
          variant: res.success ? SweepNotificationVariant.Success : SweepNotificationVariant.Error,
          message,
          details: res.success ? undefined : res.error_message,
        });
      });
    },
    [defaultEnv, dispatch, sendBiEvent, addNotification, getCrmOrgById],
  );

  const automationsDeleteCompletedHandler = useCallback(
    (
      results: {
        success: true;
        error_message?: string;
        data: {
          crmOrgId: string;
        };
      }[],
    ) => {
      if (!defaultEnv) {
        return;
      }
      dispatch(requestRefreshGlobal({ crmOrgId: defaultEnv.id }));
      const resultOfDefault = results.find((res) => res.data.crmOrgId === defaultEnv.id);

      //product is to show error only in case it wasn't deleted from default creation env
      if (!resultOfDefault || resultOfDefault.success) {
        return addNotification({
          keepOpen: false,
          variant: SweepNotificationVariant.Success,
          message: 'Successfully deleted',
        });
      } else {
        return addNotification({
          keepOpen: true,
          variant: SweepNotificationVariant.Error,
          message: `Failed to delete from ${defaultEnv.name} due to technical issues`,
        });
      }
    },
    [addNotification, defaultEnv, dispatch],
  );

  const devOpsCenterElementsDeployCompletedHandler = (
    results: {
      success: boolean;
      data: { rollupId?: string; fieldId?: string; automationId?: string; crmOrgId: string };
    }[],
  ) => {
    dispatch(setDeploying(false));
    const allSuccess = results.every((res) => res.success);
    const allFailed = results.every((res) => !res.success);
    if (allSuccess) {
      sendBiEvent({ name: ACTIONS_EVENTS.devopsCenterDeploySuccess });
      return addNotification({
        keepOpen: false,
        variant: SweepNotificationVariant.Success,
        message: `Selected elements have been successfully deployed`,
      });
    }
    if (allFailed) {
      return addNotification({
        keepOpen: true,
        variant: SweepNotificationVariant.Error,
        message: `All elements failed to deploy due to technical issues`,
      });
    }
    if (results.length !== 0) {
      return addNotification({
        keepOpen: true,
        variant: SweepNotificationVariant.Warning,
        message: `Deployment partially succeeded.`,
        action: {
          reduxAction: showDeploymentReport(results),
          actionText: 'See deployment report',
        },
      });
    }
  };

  const deploymentCompletedHandler = useCallback(
    (payload: { success: boolean; error_message: string; data: FunnelDeploymentSocketData }) => {
      if (payload.success) {
        sendBiEvent({ name: ACTIONS_EVENTS.funnelsDeploySuccess });
        dispatch(setShouldPillsRecalculate(true));
      }
      dispatch(deploymentCompleted(payload));
      dispatch(
        setFunnelStatusFromFunnel({
          id: payload.data.funnelId,
          pendingChanges: false,
          isDeployed: true,
          crmOrgId: payload.data.crmOrgId,
        }),
      );
      dispatch(requestRefreshGlobal({ crmOrgId: payload.data.crmOrgId }));
    },
    [dispatch, sendBiEvent],
  );

  const parseCompletedHandler = useCallback(
    (response: any) => {
      if (response.success) {
        fetchGlobal({ crmOrgId: response.data.crmOrgId });
      }
    },
    [fetchGlobal],
  );

  const parseBatchCompletedHandler = useCallback(
    (response: any) => {
      if (response.success) {
        fetchGlobal({ crmOrgId: response.data.crmOrgId });
        dispatch(setShouldPillsRecalculate(true));
      }
    },
    [dispatch, fetchGlobal],
  );

  const refreshHomepageDataHandler = (response: any) => {
    if (response.success) {
      fetchOrgAnalysis(response.data.crmOrgId);
    }
  };

  const fieldsUtilizationCompletedHandler = (response: any) => {
    if (response.success) {
      dispatch(requestRefreshGlobal({ crmOrgId: response.data.crmOrgId }));
    }
  };

  const issuesScanUpdateHandler = useCallback(
    (response: { data: ScanUpdate; success: boolean }) => {
      const { data } = response;
      if (crmOrgId) {
        dispatch(
          updateAgentScanStatus({
            crmOrgId,
            scanUpdate: data,
          }),
        );
      }
    },
    [dispatch, crmOrgId],
  );

  const agentFileStatusUpdatedHandler = useCallback(
    (response: {
      data: { agentId: string; fileId: string; status: AgentFileStatus };
      success: boolean;
    }) => {
      const { data } = response;
      if (crmOrgId && data.agentId && data.fileId && data.status) {
        dispatch(
          updateAgentFileStatus({
            crmOrgId,
            agentId: data.agentId,
            fileId: data.fileId,
            status: data.status,
          }),
        );

        if (data.status === AgentFileStatus.COMPLETED) {
          addNotification({
            variant: SweepNotificationVariant.Success,
            message: 'The upload and processing completed successfully',
            autoHideDuration: 6000,
          });
        } else if (data.status === AgentFileStatus.FAILED) {
          addNotification({
            variant: SweepNotificationVariant.Warning,
            message: 'Processing failed',
            autoHideDuration: 6000,
          });
        }
      }
    },
    [dispatch, crmOrgId, addNotification],
  );

  const chatNameUpdatedHandler = useCallback(
    (response: { data: { chatId: string; agentId: string; name: string }; success: boolean }) => {
      const { data } = response;
      if (!data.chatId || !data.agentId || !data.name) {
        return;
      }

      dispatch(
        setChatName({
          agentId: data.agentId,
          chatId: data.chatId,
          name: data.name,
        }),
      );
    },
    [dispatch],
  );

  const teamsInstallCompletedHandler = (payload: any, success: boolean) => {
    if (success) {
      addNotification({
        variant: SweepNotificationVariant.Success,
        message: `Successfully connected to the team`,
      });
    } else {
      addNotification({
        variant: SweepNotificationVariant.Error,
        message: `Couldn't connect – you lack permission for this team. Contact your admin to request access`,
      });
    }
  };
  const teamsUninstallCompletedHandler = (payload: any, success: boolean) => {
    if (success) {
      addNotification({
        variant: SweepNotificationVariant.Success,
        message: `Successfully disconnected from the team`,
      });
    } else {
      addNotification({
        variant: SweepNotificationVariant.Error,
        message: `Couldn't disconnect – you lack permission for this team. Contact your admin to request access`,
      });
    }
  };

  const aiProcessMiningFunnelMapCompletedHandler = async () => {
    await fetchFunnelMaps({ includeFunnelsData: true });
  };

  const aiProcessMiningAgentFunnelMapCompletedHandler = useCallback(
    (payload: { success: boolean; data: { funnelMapId: string; message: string } }) => {
      const { funnelMapId, message } = payload.data;
      if (payload.success) {
        dispatch(
          addChatMessage({
            funnelMapId,
            message: {
              role: ChatCompletionRequestMessageRole.ASSISTANT,
              content: message || 'Done! What else?', //TODO waiting for Product
              createdAt: DateTime.now().toISO(),
            },
          }),
        );
        dispatch(setMetadataMappingCompleted({ funnelMapId }));

        // Check if user is already on the target funnel editor page
        const targetRoute = `${appRoutes.funnelEditor.route}/${funnelMapId}/${accountId}`;
        const isOnTargetPage = pathname === targetRoute;

        // If user is not on the target page, show a notification with a link to the target page
        if (!isOnTargetPage) {
          addNotification({
            variant: SweepNotificationVariant.Success,
            message: 'Salesforce visual documentation was generated successfully',
            keepOpen: true,
            linkUrl: targetRoute,
            linkText: 'Click here to view the map',
            linkOpenInNewTab: false,
          });
        }
      } else {
        dispatch(
          setMetadataMappingError({
            funnelMapId,
            error: `Couldn't generate results. Try again with a new prompt.`,
          }),
        );
        addNotification({
          variant: SweepNotificationVariant.Error,
          message: `Couldn't generate results. Try again with a new prompt.`,
        });
      }
    },
    [addNotification, dispatch, pathname, accountId],
  );

  /**
   * Handles research started notification from the backend via WebSocket.
   * Creates the initial assistant message with reasoning to show research has begun.
   * This clears the loading spinner and sets up the message for subsequent progress updates.
   */
  const researchStartedHandler = useCallback(
    (response: {
      data: { chatId: string; agentId: string; message: string };
      success: boolean;
    }) => {
      const { data } = response;
      if (!data.chatId || !data.agentId) {
        console.warn('Research started missing chatId or agentId', data);
        return;
      }

      dispatch(
        setResearchInProgress({
          agentId: data.agentId,
          chatId: data.chatId,
          isResearchInProgress: true,
        }),
      );

      // Create initial assistant message with reasoning chunk
      dispatch(
        addAsyncResponseChunk({
          agentId: data.agentId,
          chatId: data.chatId,
          chunk: {
            reasoning: 'Preparing to start the research',
            isError: false,
            createdAt: new Date().toISOString(),
            role: ChatCompletionRequestMessageRole.ASSISTANT,
          },
          isFirstContentChunk: true, // Creates the message and clears loading state
        }),
      );
    },
    [dispatch],
  );

  /**
   * Handles research progress updates from the backend via WebSocket.
   * Dispatches reasoning chunks to Redux which appear as tool explanations in the chat UI.
   * Uses the "Research progress:" prefix to trigger replacement behavior (updates last progress instead of appending).
   */
  const researchProgressHandler = useCallback(
    (response: {
      data: { chatId: string; agentId: string; message: string; progressPct: number };
      success: boolean;
    }) => {
      const { data } = response;
      if (!data.chatId || !data.agentId) {
        console.warn('Research progress missing chatId or agentId', data);
        return;
      }

      dispatch(
        setResearchInProgress({
          agentId: data.agentId,
          chatId: data.chatId,
          isResearchInProgress: true,
        }),
      );

      // Dispatch reasoning chunk with agentId from WebSocket payload
      // Use isFirstContentChunk: true to:
      // 1. Clear loading state (remove spinner)
      // 2. Create assistant message if one doesn't exist for reasoning
      // The reducer's handleFirstReasoningChunk is idempotent - it won't create duplicates
      dispatch(
        addAsyncResponseChunk({
          agentId: data.agentId,
          chatId: data.chatId,
          chunk: {
            reasoning: data.message, // Already formatted with "Research progress:" prefix by backend
            isError: false,
            createdAt: new Date().toISOString(),
            role: ChatCompletionRequestMessageRole.ASSISTANT,
          },
          isFirstContentChunk: true,
        }),
      );
    },
    [dispatch],
  );

  /**
   * Handles research completion from the backend via WebSocket.
   * The result message is already saved to DB by the backend.
   * This handler adds the message to local Redux state and clears streaming state.
   */
  const researchCompletedHandler = useCallback(
    (response: {
      success: boolean;
      error_message?: string;
      data: {
        chatId: string;
        agentId: string;
        result?: { message: string; content?: AiChatMessageContent };
      };
    }) => {
      const { data, success, error_message } = response;
      if (!data.chatId || !data.agentId) {
        console.warn('Research completed missing chatId or agentId', data);
        return;
      }

      // Add the result message to local state (it's already saved to DB by backend)
      // Use structured content (with artifact/table refs) when available, fall back to text
      if (success && data.result?.message) {
        dispatch(
          addAsyncResponse({
            agentId: data.agentId,
            chatId: data.chatId,
            message: {
              content: data.result.content ?? data.result.message,
              role: ChatCompletionRequestMessageRole.ASSISTANT,
              createdAt: new Date().toISOString(),
              isError: false,
            },
          }),
        );
      } else if (!success) {
        // Handle error case - add error message to chat
        dispatch(
          addAsyncResponse({
            agentId: data.agentId,
            chatId: data.chatId,
            message: {
              content: error_message || 'Research failed. Please try again.',
              role: ChatCompletionRequestMessageRole.ASSISTANT,
              createdAt: new Date().toISOString(),
              isError: true,
            },
          }),
        );
      }

      // Clear research-in-progress flag so finishAsyncResponse can proceed
      dispatch(
        setResearchInProgress({
          agentId: data.agentId,
          chatId: data.chatId,
          isResearchInProgress: false,
        }),
      );

      // Clear streaming state
      dispatch(
        finishAsyncResponse({
          agentId: data.agentId,
          chatId: data.chatId,
        }),
      );
    },
    [dispatch],
  );

  const simulationCompletedHandler = useCallback(
    (response: {
      success: boolean;
      data: {
        crmOrgId?: string;
        tracedUserId?: string;
        error?: string;
      };
    }) => {
      const { data, success } = response;
      dispatch(
        setSimulationResult({
          success,
          crmOrgId: data?.crmOrgId,
          tracedUserId: data?.tracedUserId,
          error: data?.error,
        }),
      );
    },
    [dispatch],
  );

  return {
    fetchCompletedHandler,
    slackConnectionCompletedHandler,
    slackDisconnectionCompletedHandler,
    teamsConnectionCompletedHandler,
    teamsDisconnectionCompletedHandler,
    reportingActivationCompletedHandler,
    notificationsNewHandler,
    sfChangeFeedNotificationReceived,
    automationsDeployCompletedHandler,
    automationsDeleteCompletedHandler,
    rollupsDeployCompletedHandler,
    devOpsCenterElementsDeployCompletedHandler,
    deploymentCompletedHandler,
    parseCompletedHandler,
    parseBatchCompletedHandler,
    refreshHomepageDataHandler,
    fieldsUtilizationCompletedHandler,
    issuesScanUpdateHandler,
    agentFileStatusUpdatedHandler,
    chatNameUpdatedHandler,
    teamsInstallCompletedHandler,
    teamsUninstallCompletedHandler,
    aiProcessMiningFunnelMapCompletedHandler,
    aiProcessMiningAgentFunnelMapCompletedHandler,
    researchStartedHandler,
    researchProgressHandler,
    researchCompletedHandler,
    simulationCompletedHandler,
  };
};
export default useSocketHandlers;
